import { useQuery } from '@tanstack/react-query';
import { uniq } from 'lodash-es';
import { SovosTextField } from 'mosaic-react';
import React, { useState } from 'react';

import { checkNameNewLocationMapping } from 'src/api/locationMappings';
import FormStepLayout from 'src/components/FormStepLayout';
import type { LocationMappingEditorFormStep } from 'src/pages/locationMapping/locationMappingEditor/LocationMappingEditorTypes';

const StepFileDetails: LocationMappingEditorFormStep<{
	previousName?: string;
	description?: string | null;
	fileName?: string;
	setFileDescription: (fileDescription: string) => void;
	setFileName: (fileName: string) => void;
}> = ({
	previousName,
	description,
	fileName,
	onContinue,
	setFileDescription,
	setFileName,
}) => {
	const [showErrors, setShowErrors] = useState(false);

	const nameIsAvailableQuery = useQuery(
		['checkName', fileName],
		async () => (fileName ? checkNameNewLocationMapping(fileName) : false),
		{ enabled: false, cacheTime: 0 }
	);

	const matchedForbiddenCharacters = fileName?.match(/[%\\?%*:/|"<>.]/g);

	const validations = [
		{
			isValid: Boolean(fileName && fileName.length > 0),
			message: 'Please enter a valid file name',
		},
		{
			isValid: !matchedForbiddenCharacters,
			message: `Special characters (${uniq(matchedForbiddenCharacters)
				?.sort()
				.join(', ')}) are not allowed in the file name`,
		},
		{
			isValid:
				!fileName ||
				fileName === previousName ||
				!nameIsAvailableQuery.isFetched ||
				nameIsAvailableQuery.data,
			message: 'File name already in use, please enter a new name',
		},
	];

	const errors = validations
		.filter(({ isValid }) => !isValid)
		.map(({ message }) => message);

	const handleContinue = () => {
		if (errors.length === 0) {
			if (!nameIsAvailableQuery.isFetched && fileName !== previousName) {
				nameIsAvailableQuery.refetch().then(({ data }) => {
					if (data) {
						onContinue();
					} else if (!showErrors) {
						setShowErrors(true);
					}
				});
			} else {
				onContinue();
			}
		} else if (!showErrors) {
			setShowErrors(true);
		}
	};

	return (
		<FormStepLayout
			subTitle='Please provide the file name'
			title='File details'
			errors={errors}
			showErrors={showErrors}
			isLoading={nameIsAvailableQuery.isFetching}
			onContinue={handleContinue}
		>
			<div style={{ width: 400 }}>
				<SovosTextField
					required
					fullWidth
					onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
						setFileName(event.target.value)
					}
					label='File name'
					value={fileName || ''}
					error={showErrors && errors?.length > 0}
					sx={{
						'&&& label': {
							color:
								showErrors && errors?.length > 0 ? 'error.main' : undefined,
						},
					}}
				/>
				<SovosTextField
					fullWidth
					onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
						setFileDescription(event.target.value)
					}
					label='File description'
					value={description || ''}
				/>
			</div>
		</FormStepLayout>
	);
};

export default StepFileDetails;
